import { hasCategoryAndEventName, NetworkEventOnBeforeRequest } from "../../../lib/Events";
import { observable, action, computed, makeObservable } from "mobx";
import { tryDecodeBase64GZipToUtf8 } from "../../../lib/utils/tryDecodeBase64Gzip";
import { decode as clarityDecode } from "clarity-decode";
import { DecodedPayload as ClarityDecodedPayload } from "clarity-decode/types/data";
import { NewEventsAddedToDatabase } from "../../../State/StateEvents";
import { DbRecordedEvent, UUID } from "../../../State/EvidenceDB/DatabaseTypes";
import { DbEventsTableReader } from "../../../State/EvidenceDB/DbEventsTableReader";
import { requireEvidenceDatabase } from "../../../State/EvidenceDB/getDatabase";
import { mapGetOrCreate } from "../../../lib/utils/mapGetOrCreate";
import { urlToURL } from "../../../lib/utils/domains";
import { AnnouncementNewEventsAddedToDatabase } from "../../../constants";


const isBeforeRequestEvent: (e: DbRecordedEvent | undefined) => e is DbRecordedEvent<NetworkEventOnBeforeRequest> =
	hasCategoryAndEventName("WebRequest", "WebRequest.beforeRequest") as (e: DbRecordedEvent | undefined) => e is DbRecordedEvent<NetworkEventOnBeforeRequest>;

// const isClarityUrlRegExp = new RegExp('^https://[.]*\.clarity\.ms/collect');
export const isClarityUrl = (url: string) => url.endsWith('.clarity.ms/collect');

const isGoogleAnalyticsRegExp = new RegExp('^https://www.google-analytics\.com/[a-z]/collect');
const isGoogleAnalyticsRegExp2 = new RegExp('^https://analytics.google.com/[a-z]/collect');

const isWebArchiveGoogleAnalyticsRegExp = new RegExp('^https://web.archive.org/web/[0-9]+/https://www.google-analytics\.com/[a-z]/collect');
export const isGoogleAnalyticsUrl = (url: string) => {
	return isGoogleAnalyticsRegExp.test(url) ||
		isGoogleAnalyticsRegExp2.test(url) ||
		isWebArchiveGoogleAnalyticsRegExp.test(url);
}

// https://web.archive.org/web/20231115192722/https://y.clarity.ms/collect

type AnalyticsMessageBase<T extends string> = {
	type: T;
	networkEvent: DbRecordedEvent<NetworkEventOnBeforeRequest>;
	urlSearchParams?: Record<string, string>;
	postDataText?: string;
}

export interface ClarityAnalyticsMessage extends AnalyticsMessageBase<'clarity'> {
	decodedPayload: ClarityDecodedPayload;
}

export interface GoogleAnalyticsAnalyticsMessage extends AnalyticsMessageBase<'google-analytics'> {
	urlSearchParams: Record<string, string>;
	postTextEvents?: Record<string, string>[];
}

export type AnalyticsMessage = ClarityAnalyticsMessage | GoogleAnalyticsAnalyticsMessage;
//export type AnalyticsGetMessage = Extract<AnalyticsMessage, AnalyticsMessageBase<string>>;
//export type AnalyticsGetMessageType = Extract<AnalyticsMessage, {urlSearchParams: [string, string][]}>["type"];

export class AnalyticsTrafficData {

	private eventIndexesAlreadyAdded = new Set<number>();
	private _analyticsMessages = observable.array<AnalyticsMessage>([]);

	protected addAnalyticsMessage = action((event: AnalyticsMessage) => {
		this._analyticsMessages.push(event);
	});

	@computed get analyticsMessages() {
		return this._analyticsMessages.toSorted( (a,b) => b.networkEvent.eventIndex - a.networkEvent.eventIndex );
	}

	constructor() {
		makeObservable(this);
	}

	protected decodeAndAddClarityEvent = async (event: DbRecordedEvent<NetworkEventOnBeforeRequest>) => {
		const bytes = event.raw.requestBody?.raw?.[0]?.bytes;
		if (bytes == null) return;
		try {
			const gzipDecoded = await tryDecodeBase64GZipToUtf8(bytes) ?? 
				new TextDecoder("utf-8", { fatal: true }).decode(bytes);
			const payload = clarityDecode(gzipDecoded);
			this.addAnalyticsMessage({type: 'clarity', networkEvent: event, decodedPayload: payload});
		} catch {
			// ignore errors
		}
	}

	protected decodeAndAddGaEvent = <T extends AnalyticsMessage>(
		type: T["type"],
		networkEvent: DbRecordedEvent<NetworkEventOnBeforeRequest>
	): T | undefined => {
		// const url = networkEvent.destination.url;
		const urlObj = urlToURL(networkEvent.raw.url);
		if (urlObj == null) return;
		const urlSearchParams = Object.fromEntries(urlObj.searchParams.entries());
		const postDataArray = networkEvent.raw.requestBody?.raw?.[0]?.bytes;
		const postDataText = postDataArray == null ? undefined : new TextDecoder("utf-8", { fatal: true }).decode(postDataArray);
		const postTextEvents = postDataText == null ? undefined : 
			postDataText.split("\r\n").map( line =>
				Object.fromEntries(line.split("&").map( param => {
					const [key, encodedValue = ""] = param.split("=");
						let value = encodedValue;
						try {
							value = decodeURIComponent(encodedValue);
						} catch {
							// ignore errors
						}
						return [key,value];
					}
				)
			));
			console.log("Google Analytics Event", {postTextEvents, postDataText, networkEvent});
		const e = {type, networkEvent, urlSearchParams, postDataText, postTextEvents} as T;
		this.addAnalyticsMessage(e);
	}

	protected addBeforeRequestEvent = (beforeRequestEvent: DbRecordedEvent<NetworkEventOnBeforeRequest>) => {
		if (this.eventIndexesAlreadyAdded.has(beforeRequestEvent.eventIndex)) return;
		this.eventIndexesAlreadyAdded.add(beforeRequestEvent.eventIndex);
		if (isClarityUrl(beforeRequestEvent.destination.url)) {
			this.decodeAndAddClarityEvent(beforeRequestEvent);
		} else if (isGoogleAnalyticsUrl(beforeRequestEvent.destination.url)) {
			this.decodeAndAddGaEvent('google-analytics', beforeRequestEvent);
		}
	}

	addEvents = (events: DbRecordedEvent[]) => {
		events
			.filter(e => isBeforeRequestEvent(e))
			.forEach(e => this.addBeforeRequestEvent(e));
	}

}

export class AnalyticsTrafficDataFromDatabase extends AnalyticsTrafficData {

	protected addBeforeRequestEventAtIndex = async (eventsTableReader: DbEventsTableReader, eventIndex: number) => {
		const event = await eventsTableReader.getEvent({eventIndex});
		if (event != null  && isBeforeRequestEvent(event)) {
			this.addBeforeRequestEvent(event);
		}
	}

	protected onNewEventsListener = (eventsTableReader: DbEventsTableReader) =>(e: AnnouncementNewEventsAddedToDatabase) => {
		for (const event of e.eventsInfo) {
			if (event.category === "WebRequest" && event.eventName === "WebRequest.beforeRequest" && e.recordingUuid === this.recordingUuid) {
				this.addBeforeRequestEventAtIndex(eventsTableReader, event.eventIndex);
			}
		}
	}

	protected initialize = async () => {
		const db = await requireEvidenceDatabase();
		const eventsTableReader = DbEventsTableReader.forDbAndRecordingUuid(db, this.recordingUuid);
		NewEventsAddedToDatabase.addListener( this.onNewEventsListener(eventsTableReader) );
		this.addEvents(await eventsTableReader.getEventsForCategoryAndName({category: "WebRequest", name: "WebRequest.beforeRequest"}));
	}

	constructor(
		public readonly recordingUuid: UUID,
	) {
		super();
		this.initialize();
	}

	static #forRecordingUuid = new Map<UUID, AnalyticsTrafficDataFromDatabase>();
	static forRecordingUuid = (recordingUuid: UUID) => mapGetOrCreate(this.#forRecordingUuid, recordingUuid, () => new AnalyticsTrafficDataFromDatabase(recordingUuid));	
}
